import { apiUrl } from './config';
import { reportPossibleSessionLoss } from '@/auth/sessionLoss';

/**
 * Envelope HTTP client for the Harvest backend contract:
 * success {data, meta}, error {error: {code, message, retryable, details?}, meta}.
 * Cursors opaque; enums verbatim; unknown fields ignored.
 */

export type ApiErrorCode =
  | 'INVALID_REQUEST'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'REQUEST_TIMEOUT'
  | 'CAPABILITY_UNAVAILABLE'
  | 'EVIDENCE_INSUFFICIENT'
  | 'DEPENDENCY_UNAVAILABLE'
  | 'INTERNAL'
  | 'NETWORK'
  /** client-side: a 2xx body failed pinned-contract validation */
  | 'CONTRACT_VIOLATION';

export class ApiError extends Error {
  constructor(
    public code: ApiErrorCode,
    message: string,
    public retryable: boolean,
    public requestId: string | null,
    public status: number | null,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get capabilityUnavailable(): boolean {
    return this.code === 'CAPABILITY_UNAVAILABLE';
  }

  /** details.capability when the backend names one (stable machine identifier). */
  get capability(): string | null {
    const c = this.details?.capability;
    return typeof c === 'string' ? c : null;
  }

  /** details.reason: safe renderable explanation from the backend, verbatim. */
  get reason(): string | null {
    const r = this.details?.reason;
    return typeof r === 'string' ? r : null;
  }

  /** Hard tenant boundary (handoff 3.1): never retry, never render as empty data. */
  get tenantGated(): boolean {
    return this.capability === 'company_scoped_authorization';
  }
}


interface Meta {
  requestId: string;
  generatedAt: string;
}

async function parseEnvelope<T>(res: Response): Promise<{ data: T; meta: Meta }> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new ApiError(
      res.ok ? 'INTERNAL' : 'NETWORK',
      'The API returned a response that is not the contracted envelope.',
      false,
      res.headers.get('X-Request-Id'),
      res.status,
    );
  }
  const b = body as { data?: T; error?: { code: ApiErrorCode; message: string; retryable: boolean; details?: Record<string, unknown> }; meta?: Meta };
  if (!res.ok || b.error) {
    const e = b.error;
    // An explicit 401 is the unambiguous form of the same event the network
    // branch below can only guess at.
    if ((e?.code ?? '') === 'UNAUTHORIZED' || res.status === 401) reportPossibleSessionLoss();
    throw new ApiError(
      e?.code ?? 'INTERNAL',
      e?.message ?? 'The API returned an error without a message.',
      e?.retryable ?? false,
      b.meta?.requestId ?? res.headers.get('X-Request-Id'),
      res.status,
      e?.details,
    );
  }
  if (typeof b.meta?.requestId !== 'string' || typeof b.meta?.generatedAt !== 'string') {
    throw new ApiError(
      'CONTRACT_VIOLATION',
      'The API returned a success body without the contracted meta envelope.',
      false,
      res.headers.get('X-Request-Id'),
      res.status,
    );
  }
  return { data: b.data as T, meta: b.meta };
}

async function run<T>(path: string, init: RequestInit, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      credentials: 'include',
      ...init,
      // No client X-Request-Id header: the server generates one, and omitting
      // custom headers keeps GETs CORS-simple so they pass IAP before the
      // OPTIONS-passthrough configuration lands (mutations still preflight).
      headers: { Accept: 'application/json', ...(init.headers ?? {}) },
      signal: signal ?? init.signal ?? null,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    // Behind IAP an expired session is a redirect to accounts.google.com, and
    // fetch can only surface that as a network failure. Indistinguishable from
    // a real outage here, so this raises a suspicion rather than a verdict and
    // the auth provider re-reads the session to decide.
    reportPossibleSessionLoss();
    throw new ApiError('NETWORK', 'The API could not be reached.', true, null, null);
  }
  const { data } = await parseEnvelope<T>(res);
  return data;
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return run<T>(path, { method: 'GET' }, signal);
}

/**
 * Mutation helper: Idempotency-Key required by the backend contract; the CSRF
 * sentinel is the deliberate non-secret custom header. Origin and
 * Sec-Fetch-Site are set by the browser, never by code.
 */
export function apiPost<T>(path: string, body: unknown, idempotencyKey: string, signal?: AbortSignal): Promise<T> {
  return run<T>(
    path,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
        'X-CSRF-Token': '1',
      },
      body: JSON.stringify(body),
    },
    signal,
  );
}

/** JSON PUT with the browser mutation boundary (CSRF sentinel; PUT is idempotent by nature). */
export function apiPut<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return run<T>(
    path,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': '1',
      },
      body: JSON.stringify(body),
    },
    signal,
  );
}

/**
 * Empty-body mutation: several start operations contract `requestBody: never`
 * and reject any body, including `{}`. No Content-Type is sent.
 */
export function apiPostEmpty<T>(path: string, idempotencyKey: string, signal?: AbortSignal): Promise<T> {
  return run<T>(
    path,
    {
      method: 'POST',
      headers: {
        'Idempotency-Key': idempotencyKey,
        'X-CSRF-Token': '1',
      },
    },
    signal,
  );
}

/** Fresh high-entropy idempotency key, contract alphabet. */
export function newIdempotencyKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return `psd_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}
